import { useCallback, useEffect, useState } from 'react';

import { tradingApi } from '../services/api.ts';
import { useQuote, useQuoteConnectionStatus } from '../market/QuoteStore';
import { getPricePresentation, type PriceQuoteReference } from '../market/pricePresentation';
import {
  isFutureContract,
  isQualifiedContract,
  type ContractSelection,
} from '../components/ManualOrderEntry/contractSelection';
import { selectedContractMatchesInput } from '../components/ManualOrderEntry/orderPayload';
import { quoteToPrice } from '../components/ManualOrderEntry/quoteUtils';

interface UseManualOrderQuotesOptions {
  symbol: string;
  secType: string;
  selectedContract: ContractSelection | null;
}

/** Own quote lookups, streamed quote state, and order-price trust presentation. */
export const useManualOrderQuotes = ({ symbol, secType, selectedContract }: UseManualOrderQuotesOptions) => {
  const [marketPrice, setMarketPrice] = useState<number | null>(null);
  const [marketPriceQuote, setMarketPriceQuote] = useState<PriceQuoteReference | null>(null);
  const [fetchingPrice, setFetchingPrice] = useState(false);

  const liveQuoteIdentifier = selectedContract?.con_id
    ? `conid:${selectedContract.con_id}`
    : (selectedContract?.symbol || symbol || null);
  const liveQuote = useQuote(liveQuoteIdentifier);
  const quoteConnectionStatus = useQuoteConnectionStatus();
  const displayedMarketQuote: PriceQuoteReference | null = liveQuote || marketPriceQuote;
  const marketPricePresentation = getPricePresentation({
    quote: displayedMarketQuote,
    connectionStatus: quoteConnectionStatus,
    hasPrice: marketPrice !== null,
    streamed: Boolean(liveQuote),
  });

  const clearQuote = useCallback(() => {
    setMarketPrice(null);
    setMarketPriceQuote(null);
  }, []);

  const setQuoteSnapshot = useCallback((price: number, quote: PriceQuoteReference) => {
    setMarketPrice(price);
    setMarketPriceQuote(quote);
  }, []);

  useEffect(() => {
    if (!liveQuote || !Number.isFinite(Number(liveQuote.price))) return;
    setMarketPrice(Number(liveQuote.price));
    setMarketPriceQuote(liveQuote);
    setFetchingPrice(false);
  }, [liveQuote]);

  const fetchQuote = useCallback(async (targetSymbols: string | string[]) => {
    const symbols = (Array.isArray(targetSymbols) ? targetSymbols : [targetSymbols])
      .map((value) => String(value || '').trim().toUpperCase())
      .filter(Boolean);
    const uniqueSymbols = Array.from(new Set(symbols));
    if (uniqueSymbols.length === 0) {
      clearQuote();
      return;
    }
    setFetchingPrice(true);
    try {
      const response = await tradingApi.getQuotes(uniqueSymbols);
      const quotes = response.data;
      if (response.success && quotes) {
        const quote = uniqueSymbols.map((candidate) => quotes[candidate]).find(Boolean);
        const quotePrice = quoteToPrice(quote);
        if (quotePrice !== null) setQuoteSnapshot(quotePrice, quote as PriceQuoteReference);
      }
    } catch (error) {
      console.warn('Failed to fetch quote:', error);
    } finally {
      setFetchingPrice(false);
    }
  }, [clearQuote, setQuoteSnapshot]);

  const fetchContractQuote = useCallback(async (contract: ContractSelection, preferLive = false) => {
    if (!contract.symbol) return;
    setFetchingPrice(true);
    try {
      const response = await tradingApi.getContractQuote({
        symbol: contract.symbol.toUpperCase(),
        sec_type: contract.sec_type || secType,
        con_id: contract.con_id ?? undefined,
        exchange: contract.exchange ?? undefined,
        currency: contract.currency ?? undefined,
        last_trade_date_or_contract_month: contract.last_trade_date_or_contract_month ?? undefined,
        local_symbol: contract.local_symbol ?? undefined,
        trading_class: contract.trading_class ?? undefined,
        multiplier: contract.multiplier ?? undefined,
        prefer_live: preferLive,
      });
      if (response.success && response.data) {
        const quotePrice = quoteToPrice(response.data);
        if (quotePrice !== null) {
          setQuoteSnapshot(quotePrice, response.data as PriceQuoteReference);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch contract quote:', error);
    } finally {
      setFetchingPrice(false);
    }
  }, [secType, setQuoteSnapshot]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!symbol) return;
      const quoteSymbol = selectedContractMatchesInput(selectedContract, symbol)
        ? selectedContract
        : symbol;
      if (!quoteSymbol) return;
      if (typeof quoteSymbol === 'string') {
        if (secType === 'FUT') return;
        void fetchQuote(quoteSymbol);
      } else if (isQualifiedContract(quoteSymbol)) {
        void fetchContractQuote(quoteSymbol, isFutureContract(quoteSymbol));
      } else {
        void fetchQuote(quoteSymbol.symbol || '');
      }
    }, 800);
    return () => window.clearTimeout(timer);
  }, [symbol, secType, selectedContract, fetchContractQuote, fetchQuote]);

  const refreshSelectedQuote = useCallback(() => {
    if (selectedContractMatchesInput(selectedContract, symbol) && selectedContract) {
      if (isQualifiedContract(selectedContract)) {
        void fetchContractQuote(selectedContract, isFutureContract(selectedContract));
        return;
      }
    }
    if (secType === 'FUT') return;
    void fetchQuote(symbol);
  }, [selectedContract, symbol, fetchContractQuote, secType, fetchQuote]);

  return {
    marketPrice,
    setMarketPrice,
    fetchingPrice,
    marketPricePresentation,
    clearQuote,
    setQuoteSnapshot,
    fetchQuote,
    fetchContractQuote,
    refreshSelectedQuote,
  };
};
